/**
 * SelectedImagesList Component
 * 
 * Displays selected images in a 2-column grid with:
 * - Remove button (top-right)
 * - Download button (bottom-right)
 * - Source badge (bottom-left)
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import type { SelectedImagesListProps, ArticleImage } from './types';
import { THEME_COLORS } from './constants';

export const SelectedImagesList: React.FC<SelectedImagesListProps> = ({
  images,
  onRemove,
  maxImages,
}) => {
  const count = images.length;
  const isAtMax = count >= maxImages;

  // Empty state
  if (count === 0) {
    return (
      <Box
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 1,
          border: `1px dashed ${THEME_COLORS.border}`,
          textAlign: 'center',
          color: THEME_COLORS.textMuted,
        }}
      >
        <ImageIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2">
          No images selected. Search and click images to attach them to this article.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header with count */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: THEME_COLORS.textPrimary, fontWeight: 600 }}>
          Selected Images
        </Typography>
        <Chip
          label={`${count} of ${maxImages}`}
          size="small"
          sx={{
            bgcolor: isAtMax ? THEME_COLORS.warning : THEME_COLORS.accent,
            color: isAtMax ? THEME_COLORS.background : THEME_COLORS.background,
            fontWeight: 600,
          }}
        />
      </Box>

      {/* Max warning */}
      {isAtMax && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 2,
            color: THEME_COLORS.warning,
          }}
        >
          Maximum {maxImages} images allowed. Remove an image to add another.
        </Typography>
      )}

      {/* Images 2-column grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        {images.map((image) => (
          <SelectedImageItem
            key={image.id}
            image={image}
            onRemove={() => onRemove(image.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Individual selected image item — larger card with download overlay
 */
interface SelectedImageItemProps {
  image: ArticleImage;
  onRemove: () => void;
}

const SelectedImageItem: React.FC<SelectedImageItemProps> = ({ image, onRemove }) => {
  /**
   * Get the image URL for display.
   * For stock images (pixabay/pexels), use backend download endpoint to avoid expired URLs.
   * For AI-generated images, use the backend serving URL.
   */
  const getImageUrl = (): string => {
    // If image has s3_key, use S3 serving URL (for AI-generated and persisted stock images)
    if (image.s3_key) {
      // URL should already be the S3 serving URL, but ensure it's correct
      if (image.source === 'fal_ai') {
        return image.url || `/api/images/generated/${image.s3_key}`;
      } else if (image.source === 'pixabay' || image.source === 'pexels') {
        return image.url || `/api/images/stock/${image.s3_key}`;
      }
    }
    
    // Fallback for images without s3_key
    return image.url || '';
  };

  /**
   * Download the full-size image.
   * For stock images, use backend download endpoint to avoid expired URLs.
   */
  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If image has s3_key, use S3 serving URL (for AI-generated and persisted stock images)
    if (image.s3_key && image.url) {
      const link = document.createElement('a');
      link.href = image.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const extension = image.source === 'fal_ai' ? 'png' : 'jpg';
      link.download = `${image.source}-${image.id}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    
    // Fallback for images without s3_key
    if (image.url) {
      window.open(image.url, '_blank');
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1.5,
        overflow: 'hidden',
        border: `1px solid ${THEME_COLORS.border}`,
        bgcolor: THEME_COLORS.background,
        '&:hover': {
          borderColor: THEME_COLORS.accent,
          '& .overlay-buttons': {
            opacity: 1,
          },
        },
      }}
    >
      <img
        src={getImageUrl()}
        alt={image.tags?.join(', ') || 'Selected image'}
        style={{
          width: '100%',
          display: 'block',
        }}
        onError={(e) => {
          // Show error placeholder if image fails to load
          const img = e.target as HTMLImageElement;
          img.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200"><rect fill="%23333" width="400" height="200"/><text fill="%23666" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="14">Image unavailable</text></svg>';
        }}
      />

      {/* Remove button — top right */}
      <Tooltip title="Remove image" placement="top">
        <IconButton
          className="overlay-buttons"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            opacity: 0.8,
            transition: 'opacity 0.2s',
            '&:hover': {
              bgcolor: THEME_COLORS.error,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* Download button — bottom right */}
      <Tooltip title="Download full size" placement="top">
        <IconButton
          className="overlay-buttons"
          onClick={handleDownload}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            opacity: 0.8,
            transition: 'opacity 0.2s',
            '&:hover': {
              bgcolor: THEME_COLORS.accent,
            },
          }}
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* Source badge — bottom left */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          bgcolor: 'rgba(0,0,0,0.7)',
          color: 'white',
          fontSize: '0.7rem',
          px: 1,
          py: 0.3,
          borderRadius: 0.5,
          textTransform: 'capitalize',
        }}
      >
        {image.source === 'fal_ai' ? '🤖 AI Generated' : `📷 ${image.source}`}
      </Box>
    </Box>
  );
};

export default SelectedImagesList;
